<script setup>
import { computed, ref } from 'vue'
import { Link, usePage } from '@inertiajs/vue3'
import {
  Home, CreditCard, Wrench, Plane, FileText, Headphones,
  LogOut, Menu, X, Bell, ChevronDown, Lock,
} from 'lucide-vue-next'
import Logo from '../Components/Logo.vue'
import logo from '../images/logowebp.webp'

const menu = [
  { label: 'Visão Geral',     href: '/',            icon: Home },
  { label: 'Pagamentos',      href: '/pagamentos',  icon: CreditCard },
  { label: 'Manutenção',      href: '/manutencao',  icon: Wrench },
  { label: 'Mapa da Aeronave', href: '/mapa',       icon: Plane },
  { label: 'Documentos',      href: '/documentos',  icon: FileText },
  { label: 'Suporte',         href: '/suporte',     icon: Headphones },
]

const drawerOpen = ref(false)
const page = usePage()

// Unidade que este processo atende (RP ou Minas). Chega como prop global do
// Inertia (ApplicationController#inertia_share), porque este layout é
// persistente e não recebe props de página.
const unit = computed(() => page.props.unit ?? {})

// Cliente logado (ApplicationController#inertia_share).
const client = computed(() => page.props.client ?? {})
const unread = computed(() => page.props.unreadMessages ?? 0)
const firstName = computed(() => (client.value.name ?? '').split(' ')[0])
const initials = computed(() =>
  (client.value.name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join(''),
)

// page.url é reativo — a sidebar é persistente entre navegações Inertia.
const currentPath = computed(() => page.url.split('?')[0])
const isActive = (href) =>
  href === '/' ? currentPath.value === '/' : currentPath.value.startsWith(href)
</script>

<template>
  <div class="min-h-screen bg-up-bg font-sans text-up-navy">
    <!-- Overlay do drawer (mobile) -->
    <div
      v-if="drawerOpen"
      class="fixed inset-0 z-30 bg-up-navy/50 lg:hidden"
      @click="drawerOpen = false"
    />

    <!-- Sidebar -->
    <aside
      class="fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-up-navy transition-transform duration-200 lg:translate-x-0"
      :class="drawerOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <div class="flex items-center justify-between px-6 pt-6 pb-8">
        <img :src="logo" alt="UP Aviation" class="h-14 w-auto" />
        <button
          type="button"
          class="text-white/70 hover:text-white lg:hidden"
          aria-label="Fechar menu"
          @click="drawerOpen = false"
        >
          <X class="h-5 w-5" />
        </button>
      </div>

      <nav class="flex-1 overflow-y-auto" aria-label="Menu principal">
        <Link
          v-for="item in menu"
          :key="item.href"
          :href="item.href"
          class="relative flex items-center gap-3 px-6 py-3.5 text-sm tracking-wide uppercase transition-colors"
          :class="isActive(item.href)
            ? 'bg-white/10 font-semibold text-white'
            : 'text-white/70 hover:bg-white/5 hover:text-white'"
          :aria-current="isActive(item.href) ? 'page' : undefined"
          @click="drawerOpen = false"
        >
          <span
            v-if="isActive(item.href)"
            class="absolute inset-y-0 left-0 w-1 bg-up-orange"
            aria-hidden="true"
          />
          <component :is="item.icon" class="h-5 w-5 shrink-0" aria-hidden="true" />
          {{ item.label }}
        </Link>
      </nav>

      <div class="border-t border-white/10 px-6 py-5">
        <Link
          href="/sair"
          method="delete"
          as="button"
          type="button"
          class="flex w-full items-center gap-3 text-sm uppercase tracking-wide text-white/70 transition-colors hover:text-white"
        >
          <LogOut class="h-5 w-5" aria-hidden="true" />
          Sair
        </Link>
      </div>
    </aside>

    <!-- Coluna de conteúdo -->
    <div class="flex min-h-screen flex-col lg:pl-64">
      <header class="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-slate-200 bg-white px-4 sm:px-6">
        <button
          type="button"
          class="text-up-navy lg:hidden"
          aria-label="Abrir menu"
          @click="drawerOpen = true"
        >
          <Menu class="h-6 w-6" />
        </button>

        <nav aria-label="Você está em" class="flex items-center gap-4 text-sm">
          <span class="hidden font-semibold uppercase tracking-[0.18em] text-up-navy sm:inline">{{ unit.city }}</span>
          <span class="hidden h-5 w-px bg-slate-200 sm:inline-block" aria-hidden="true" />
          <span class="text-slate-500">ÁREA DO CLIENTE</span>
        </nav>

        <div class="ml-auto flex items-center gap-4">
          <!--
            O sino leva ao Suporte e só acende quando há mensagem da equipe
            ainda não lida. Antes era um ponto fixo, que acendia sempre.
          -->
          <Link
            href="/suporte"
            class="relative text-up-navy"
            :aria-label="unread
              ? `Suporte: ${unread} mensagem(ns) não lida(s)`
              : 'Suporte: nenhuma mensagem não lida'"
          >
            <Bell class="h-5 w-5" aria-hidden="true" />
            <span
              v-if="unread"
              class="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-up-orange ring-2 ring-white"
            />
          </Link>
          <span class="hidden h-8 w-px bg-slate-200 sm:inline-block" aria-hidden="true" />
          <button type="button" class="flex items-center gap-2 text-sm">
            <span
              class="flex h-9 w-9 items-center justify-center rounded-full bg-up-navy text-xs font-semibold text-white"
              aria-hidden="true"
            >{{ initials }}</span>
            <span class="hidden text-slate-600 sm:inline">Olá, <span class="font-medium text-up-navy">{{ firstName }}</span></span>
            <ChevronDown class="h-4 w-4 text-slate-400" aria-hidden="true" />
          </button>
        </div>
      </header>

      <main class="flex-1 px-4 py-6 sm:px-6">
        <slot />
      </main>

      <footer class="border-t border-slate-200 bg-white px-4 py-5 sm:px-6">
        <div class="flex flex-col items-center gap-3 text-xs text-slate-500 lg:flex-row lg:justify-between">
          <p class="flex items-center gap-2">
            <Logo class="h-4 text-up-navy" />
            <span class="text-slate-300" aria-hidden="true">|</span>
            <span class="uppercase tracking-[0.14em]">{{ unit.city }}</span>
          </p>
          <p class="flex items-center gap-2">
            <Lock class="h-4 w-4" aria-hidden="true" />
            Ambiente seguro · Seus dados protegidos
          </p>
          <p class="tracking-[0.18em] text-slate-400">VOAR SEMPRE MAIS LONGE</p>
        </div>
      </footer>
    </div>
  </div>
</template>
